import Axios from 'axios';
import { isString, isObject } from 'lodash';

import { API_CLOUD_EZLO_REQUEST_URL } from 'constants/URLs';
import { axiosCloudConfig } from '../config';
import { CALLS, ERROR_STATUS, REQUEST_PARAMS_TYPE } from 'constants/api';
import { t } from 'helpers/language';
import { EZLOGIC_TITLE_SOMETHING_WENT_WRONG } from 'constants/language_tokens';

/**
 * Validates parameters for the delete account API request.
 * @param {Object} params
 * @param {string} params.type - Must be 'delete'
 * @throws {Error}
 */
const validateDeleteAccountParams = (params) => {
    if (!params || !isObject(params)) {
        throw new Error('params must be an object');
    }

    if (!isString(params.type) || params.type !== REQUEST_PARAMS_TYPE.DELETE) {
        throw new Error(`params.type must be "${REQUEST_PARAMS_TYPE.DELETE}"`);
    }
};

/**
 * Builds the request payload for account deletion.
 * @returns {Object}
 */
const buildDeleteAccountPayload = () => {
    const params = { type: REQUEST_PARAMS_TYPE.DELETE };
    validateDeleteAccountParams(params);

    return {
        call: CALLS.USER_REQUEST,
        params,
    };
};

/**
 * Sends a request to permanently delete the current user's account.
 * The request is evaluated by customer service and may take up to a week to process.
 * On success, the user receives an email confirmation and is logged out.
 *
 * @see {@link https://confluence.mios.com/display/EPD/User+Request+-+Cancellation}
 * @returns {Promise<Object>} API response
 * @throws {Error} If the request fails or returns an error status
 */
export const apiDeleteAccount = async () => {
    const payload = buildDeleteAccountPayload();
    const response = await Axios.post(API_CLOUD_EZLO_REQUEST_URL(), payload, axiosCloudConfig());

    if (response?.data?.status === ERROR_STATUS) {
        throw new Error(
            response.data.data?.error_text ||
                response.data.data?.error_message ||
                t(EZLOGIC_TITLE_SOMETHING_WENT_WRONG),
        );
    }

    return response;
};
